import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import './ProbabilityScaleModal.css';

/**
 * Read-only view of the configured probability buckets, mirroring the
 * Cost Impact Scale modal in CustomFieldsRenderer.
 */
const ProbabilityScaleModal = ({ show, onClose, probabilityField, currentValue }) => {
  // Highest score first, matching the cost impact scale ordering
  const buckets = useMemo(() => {
    const options = probabilityField?.options || [];
    return [...options]
      .filter((option) => option && option.score !== null && option.score !== undefined)
      .sort((a, b) => b.score - a.score);
  }, [probabilityField]);

  if (!show) return null;

  const label = probabilityField?.label || probabilityField?.name || 'Probability';

  return createPortal(
    <div className="probability-scale-modal-overlay" onClick={onClose}>
      <div className="probability-scale-modal" onClick={e => e.stopPropagation()}>
        <div className="probability-scale-modal-header">
          <h3>{label} Scale</h3>
          <button
            type="button"
            className="probability-scale-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <div className="probability-scale-modal-body">
          {buckets.length > 0 ? (
            <div className="probability-scale-list">
              {buckets.map((option) => {
                const isCurrent = currentValue !== null
                  && currentValue !== undefined
                  && currentValue !== ''
                  && String(option.option_value) === String(currentValue);
                return (
                  <div
                    key={option.id ?? option.option_value}
                    className={`probability-scale-item ${isCurrent ? 'current' : ''}`}
                  >
                    <div className="probability-scale-score">{option.score}</div>
                    <div className="probability-scale-info">
                      <div className="probability-scale-label">{option.option_label}</div>
                    </div>
                    {isCurrent && <span className="probability-scale-current-badge">Selected</span>}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="probability-scale-empty">
              <i className="bi bi-exclamation-circle"></i>
              <p>Probability scale is not configured.</p>
            </div>
          )}

          <div className="probability-scale-note">
            <i className="bi bi-info-circle"></i>
            <div>
              Probability drives the vertical axis of the risk matrix. These buckets can be
              configured in Admin Settings under Custom Fields. Contact your system administrator to
              modify them.
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

ProbabilityScaleModal.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  probabilityField: PropTypes.shape({
    name: PropTypes.string,
    label: PropTypes.string,
    options: PropTypes.array
  }),
  currentValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

ProbabilityScaleModal.defaultProps = {
  show: false,
  probabilityField: null,
  currentValue: null
};

export default ProbabilityScaleModal;
